import * as React from "react"
import { Command as CmdkCommand } from "cmdk"
import { Search } from "lucide-react"
import { cn } from "@lib/cn"
import { Dialog } from "./Dialog"

const Root = React.forwardRef<
  React.ElementRef<typeof CmdkCommand>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand>
>(({ className, ...props }, ref) => (
  <CmdkCommand
    ref={ref}
    className={cn(
      "flex size-full flex-col overflow-hidden rounded-md",
      "bg-white text-cloverhound-gray-900 dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50",
      className,
    )}
    {...props}
  />
))
Root.displayName = "Command.Root"

const Input = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.Input>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center gap-2 border-b border-cloverhound-gray-250 px-3 dark:border-cloverhound-gray-800" cmdk-input-wrapper="">
    <Search className="size-4 shrink-0 text-cloverhound-gray-600" aria-hidden="true" />
    <CmdkCommand.Input
      ref={ref}
      className={cn(
        "ui-control flex h-11 w-full bg-transparent py-3 text-small",
        "outline-none focus:outline-none focus:ring-0 focus:shadow-none focus:border-transparent",
        "placeholder:text-cloverhound-gray-600",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
))
Input.displayName = "Command.Input"

const List = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.List>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.List>
>(({ className, ...props }, ref) => (
  <CmdkCommand.List
    ref={ref}
    className={cn("max-h-[22rem] overflow-y-auto overflow-x-hidden p-1", className)}
    {...props}
  />
))
List.displayName = "Command.List"

const Empty = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.Empty>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.Empty>
>(({ className, ...props }, ref) => (
  <CmdkCommand.Empty
    ref={ref}
    className={cn("py-8 text-center text-small text-cloverhound-gray-600", className)}
    {...props}
  />
))
Empty.displayName = "Command.Empty"

const Group = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.Group>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.Group>
>(({ className, ...props }, ref) => (
  <CmdkCommand.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-cloverhound-gray-900 dark:text-cloverhound-gray-50",
      "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-cloverhound-gray-500",
      className,
    )}
    {...props}
  />
))
Group.displayName = "Command.Group"

const Item = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.Item>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.Item>
>(({ className, ...props }, ref) => (
  <CmdkCommand.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-small outline-none",
      "data-[selected=true]:bg-cloverhound-gray-100 data-[selected=true]:text-cloverhound-gray-900",
      "dark:data-[selected=true]:bg-cloverhound-gray-800 dark:data-[selected=true]:text-cloverhound-gray-50",
      "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      className,
    )}
    {...props}
  />
))
Item.displayName = "Command.Item"

const Separator = React.forwardRef<
  React.ElementRef<typeof CmdkCommand.Separator>,
  React.ComponentPropsWithoutRef<typeof CmdkCommand.Separator>
>(({ className, ...props }, ref) => (
  <CmdkCommand.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-cloverhound-gray-200 dark:bg-cloverhound-gray-800", className)}
    {...props}
  />
))
Separator.displayName = "Command.Separator"

const Shortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn("ml-auto text-micro tracking-widest text-cloverhound-gray-600", className)} {...props} />
)

/**
 * Dialog wrapper for command palettes (Cmd+K style).
 * Compose: <Command.Dialog open={} onOpenChange={}><Command.Input ... /><Command.List>...</Command.List></Command.Dialog>
 */
interface CommandDialogProps extends React.ComponentProps<typeof Dialog.Root> {
  label?: string
  children: React.ReactNode
}

const CommandDialog = ({ label = "Command menu", children, ...props }: CommandDialogProps) => (
  <Dialog.Root {...props}>
    <Dialog.Content size="md" className="p-0" hideClose withBackdrop>
      <Root label={label}>{children}</Root>
    </Dialog.Content>
  </Dialog.Root>
)

export const Command = { Root, Input, List, Empty, Group, Item, Separator, Shortcut, Dialog: CommandDialog }
